import { Fragment, useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../services/axios";
import { useAuth } from "../../context/AuthContext";
import Pagination from "../../component/Pagination";
import utils from "../../utils/utils";
import "./MyReport.css";
import IconFilter from "../../component/SvgIcons/IconFilter";

export default function TicketsList() {
  /** ---------- Constants ---------- **/
  const allTabs = [
    { key: "inProgress", label: "Escalation In Progress" },
    { key: "closed", label: " Escalation Closed Tickets" },
  ];

  /** ---------- State ---------- **/
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState({ inProgress: 1, closed: 1 });
  const [rowData, setRowData] = useState([]);
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [itemsPerPage] = useState(window.innerWidth >= 1920 ? 16 : 10);
  const [selectedTab, setSelectedTab] = useState("inProgress");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  /** ---------- Hooks ---------- **/
  const { authUser } = useAuth();
  const navigate = useNavigate();
  const indicatorRef = useRef(null);
  const buttonsRef = useRef([]);
  const handlePageChange = (page) => {
    console.log("Page clicked:", page);
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  /** ---------- Tab Sync from URL ---------- **/
  useEffect(() => {
    const query = window.location.search?.split("?")[1];
    const filterId = query?.split("&")[1]?.split("=")[1] || "";
    if (filterId === "1") setSelectedTab("inProgress");
    if (filterId === "2") setSelectedTab("closed");
  }, []);

  /** ---------- Indicator Animation ---------- **/
  useEffect(() => {
    const index = allTabs.findIndex((t) => t.key === selectedTab);
    const button = buttonsRef.current[index];
    if (button && indicatorRef.current) {
      indicatorRef.current.style.width = `${button.offsetWidth}px`;
      indicatorRef.current.style.transform = `translateX(${button.offsetLeft}px)`;
    }
  }, [selectedTab]);

  /** ---------- Save recent search ---------- **/
  useEffect(() => {
    utils.saveRecentSearch("escalations ticket", "/escalations-ticket");
  }, []);

  /** ---------- Fetch tickets & agents ---------- **/
  useEffect(() => {
    const fetchTickets = async () => {
      setIsLoading(true);
      try {
        let payload = {};
        // const payload = authUser.isAdmin
        //   ? { isAdmin: true }
        //   : {
        //       isAdmin: false,
        //       Code: authUser?.Code,
        //       Agents_Company: authUser?.Agents_Company,
        //     };

        payload.url = window.location.search?.split("?")[1];
        const res = await api.post("/list_ticket", payload);

        if (Array.isArray(res.data.data)) {
          let targetEscalation = "";
          if (["Gulf Oil", "Global Gulf Support"].includes(authUser?.Agents_Company)) {
            targetEscalation = "Gulf";
          } else if (authUser?.Agents_Company === "Global Software") {
            targetEscalation = "Global Software";
          }

          const filtered = res.data.data
            .filter((ticket) => ticket.escalated_to === targetEscalation)
            .reduce((acc, ticket) => {
              const existing = acc[ticket.Ticket_No];
              if (!existing || new Date(ticket.updated_at) > new Date(existing.updated_at)) {
                acc[ticket.Ticket_No] = ticket;
              }
              return acc;
            }, {});

          setRowData(Object.values(filtered));
        } else {
          setRowData([]);
        }
      } catch (error) {
        console.error("Failed to fetch tickets:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchAgents = async () => {
      try {
        const res = await api.post("/list_agent");
        if (Array.isArray(res.data.data)) setAgents(res.data.data);
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    };

    fetchTickets();
    fetchAgents();
  }, [authUser]);

  /** ---------- Helpers ---------- **/
  const getAgentName = (Code) => {
    const found = agents.find((a) => a.Code === Number(Code));
    return found ? found.Agent_Name : `User ${Code}`;
  };

  const handleSort = (column) => {
    setSortConfig((prev) =>
      prev.key === column
        ? { ...prev, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key: column, direction: "asc" }
    );
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("en-GB").replaceAll("/", "-") +
      " " +
      date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true })
    );
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const truncateWords = (text = "", count = 4) => {
    const words = text.split(" ");
    return words.slice(0, count).join(" ") + (words.length > count ? "..." : "");
  };

  /** ---------- Filter & Sort ---------- **/
  const filteredData = rowData
    .filter((row) =>
      Object.values(row || {}).some((val) =>
        val?.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )
    )
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      const isNumber = !isNaN(aValue) && !isNaN(bValue);
      return isNumber
        ? sortConfig.direction === "asc"
          ? Number(aValue) - Number(bValue)
          : Number(bValue) - Number(aValue)
        : sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

  /** ---------- Group tickets ---------- **/
  const groupedData = useMemo(() => {
    const groups = {};
    filteredData.forEach((item) => {
      if (!groups[item.Ticket_No]) groups[item.Ticket_No] = [];
      groups[item.Ticket_No].push(item);
    });
    return Object.entries(groups).map(([ticketNo, items]) => ({
      ticketNo,
      items,
    }));
  }, [filteredData]);

  /** ---------- Split tabs ---------- **/
  const inProgressTickets = groupedData.filter((g) => g.items[0].Status_Name !== "Closed");
  const closedTickets = groupedData.filter((g) => g.items[0].Status_Name === "Closed");

  const activeTickets = selectedTab === "inProgress" ? inProgressTickets : closedTickets;

  /** ---------- Pagination ---------- **/
  const totalPages = Math.ceil(activeTickets.length / itemsPerPage);
  const currentTabPage = currentPage[selectedTab];
  const paginatedTickets = activeTickets.slice(
    (currentTabPage - 1) * itemsPerPage,
    currentTabPage * itemsPerPage
  );

  /** ---------- Render ---------- **/
  const renderTicketRows = (tickets) =>
    tickets.map(({ ticketNo, items }) => {
      const latestItem = items[0];
      return (
        <Fragment key={ticketNo}>
          <tr onClick={() => navigate(`/view-ticket-history/${latestItem.Ticket_No}`)}>
            <td>{ticketNo}</td>
            <td>{formatDateTime(latestItem.Create_Date_Time_Stamp)}</td>
            <td>{latestItem.Customer_Name}</td>
            <td>{getAgentName(latestItem.User)}</td>
            <td>{truncateWords(stripHtml(latestItem.Issue_Details), 4)}</td>
            <td>
              <Link
                to="#"
                className={`statusbtn ${latestItem.Status_Name?.toLowerCase()}`}
              >
                {latestItem.Status_DisplayName}
              </Link>
            </td>
          </tr>
        </Fragment>
      );
    });

  return (
    <div className="layout">
      <div className="main-content">
        <div className="dashboard">
          {/* Tabs */}
          <div className="tab-wrapper">
            <div className="tab-indicator" ref={indicatorRef} />
            {allTabs.map((tab, i) => (
              <button
                key={tab.key}
                ref={(el) => (buttonsRef.current[i] = el)}
                className={`tab-button ${selectedTab === tab.key ? "active" : ""}`}
                onClick={() => setSelectedTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Table */}

            <div className="tablespace mt-4 ">
                  <div className="table-responsive">
          <table className="table">
            
            <thead>
              <tr>
                <th onClick={() => handleSort("Ticket_No")}>Ticket No   <IconFilter /></th>
                  <th onClick={() => handleSort("Create_Date_Time_Stamp")}>Date & Time   <IconFilter /></th>
                    <th onClick={() => handleSort("Customer_Name")}>Customer Name   <IconFilter /></th>
                      <th onClick={() => handleSort("User")}>User  <IconFilter /></th>
                        <th onClick={() => handleSort("Issue_Details")}>Issue   <IconFilter /></th>
 <th onClick={() => handleSort("Ticket_No")}>Status  <IconFilter /></th>
              
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6">Loading...</td>
                </tr>
              ) : paginatedTickets.length ? (
                renderTicketRows(paginatedTickets)
              ) : (
                <tr>
                  <td colSpan="6">
                    {selectedTab === "inProgress"
                      ? "No in-progress tickets"
                      : "No closed tickets"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
</div></div>

          

          {/* Pagination */}
            {totalPages > 1 && (
                         <Pagination
                           totalPages={totalPages}
                           currentPage={currentPage}
                           handlePageChange={handlePageChange}
                         />
                       )}
        </div>
      </div>
    </div>
  );
}
